import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, getArticlesByCategory, type CategorySlug } from '@/mocks/articles';

const validSlugs: CategorySlug[] = ['entertainment', 'gossip', 'social', 'exclusive'];

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState<string>(slug || '');

  useEffect(() => {
    setActiveSlug(slug || '');
  }, [slug]);

  const isValid = validSlugs.includes(activeSlug as CategorySlug);
  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryArticles = isValid ? getArticlesByCategory(activeSlug) : [];
  const canonicalUrl = `${window.location.origin}/category/${activeSlug}`;

  const pageTitle = currentCategory ? `${currentCategory.name} - 51吃瓜网` : '分类 - 51吃瓜网';
  const pageDesc = currentCategory ? `${currentCategory.description}，尽在51吃瓜网。浏览最新${currentCategory.name}资讯。` : '51吃瓜网文章分类浏览';

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={`51吃瓜网,${currentCategory?.name || '分类'},吃瓜爆料,娱乐资讯`} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={currentCategory?.description || '51吃瓜网文章分类浏览'} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta name="twitter:card" content="summary" />

      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory?.name || '分类' },
            ]}
          />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-2">
              {currentCategory?.name || '分类'}
            </h1>
            <p className="text-foreground-600 text-sm">
              {currentCategory?.description || '浏览所有分类'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8 px-1 py-1 bg-background-100 rounded-full w-fit">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'text-foreground-600 hover:text-primary-600'
                }`}
              >
                <i className={`${cat.icon} w-4 h-4 mr-1.5 inline-flex items-center justify-center align-text-bottom`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {isValid && categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-200 rounded-full">
                <i className="ri-emotion-sad-line text-foreground-400 w-8 h-8 flex items-center justify-center"></i>
              </div>
              <p className="text-foreground-500 text-sm">暂无相关文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}